import { useSelector } from 'react-redux';
import { IGlobalState } from '../../../model/IGlobalState';

// UI only for now (user decision 2026-09-24): Client settings phase 1 has no api calls. Everything below is the spec's
// placeholder content; only the client's legal identity (name, ticker, exchange) and website come from state.currentStock.

export const NOT_CONNECTED = 'Preview only: this isn’t connected yet, so nothing was changed';

export type Role = 'Admin' | 'Approver' | 'Editor' | 'Viewer';

export const ROLES: { role: Role; badge: string; can: string; invite: string }[] = [
    { role: 'Admin', badge: 'admin', can: 'Everything for this client, including settings and access.', invite: 'Everything, including settings, billing and inviting others' },
    { role: 'Editor', badge: 'editor', can: 'Creates announcements, campaigns and pages. Can’t approve or change settings.', invite: 'Website content, campaigns and investor questions. No settings or registry export.' },
    { role: 'Approver', badge: 'approver', can: 'Approves announcement emails and answers to investor questions.', invite: 'Can approve and send announcement emails, plus everything an editor can do' },
    { role: 'Viewer', badge: 'viewer', can: 'Read‑only: registry, reports and analytics.', invite: 'Read‑only. Good for directors who just want to look.' },
];

export interface Person {
    id: string;
    name: string;
    email: string;
    organisation: string;
    role: Role;
    lastActive: string;
    you?: boolean;
}

export interface Invite {
    id: string;
    email: string;
    role: Role;
    invited: string;
}

export const PEOPLE = (company: string): Person[] => [
    { id: 'lf', name: 'Lily Ferliansyah', email: 'lily.ferliansyah@oliveragency.com.au', organisation: 'Oliver Agency', role: 'Admin', lastActive: 'Now', you: true },
    { id: 'wt', name: 'Wallace Turner', email: 'wallace.turner@oliveragency.com.au', organisation: 'Oliver Agency', role: 'Editor', lastActive: '2 hours ago' },
    { id: 'st', name: 'Sam Taylor', email: 's.taylor@49metals.com.au', organisation: company, role: 'Approver', lastActive: 'Yesterday' },
    { id: 'cr', name: 'Cameron Reid', email: 'cameron@reidconsulting.example', organisation: 'Reid Consulting', role: 'Viewer', lastActive: '3 days ago' },
];

export const INVITES: Invite[] = [
    { id: 'editor', email: 'editor@oliveragency.com.au', role: 'Editor', invited: 'invited 2 days ago by Lily Ferliansyah' },
];

export const ACCESS_LOG = [
    { what: 'Vanessa Lim invited as Editor', when: '2 days ago · Lily' },
    { what: 'Cameron Reid changed to Viewer', when: '12 Aug · Lily' },
    { what: 'Sam Taylor added as Approver', when: '3 Mar · Lily' },
];

export const HOLDERS = 772;

export interface Plan {
    id: 'starter' | 'growth' | 'enterprise';
    name: string;
    summary: string;
    // per year + GST; null = priced by Investi per client
    price: string | null;
    // the plan below's name: the list opens with "Everything in {includes}" and then shows only what this plan adds
    includes?: string;
    features: string[];
}

// placeholder plans: Growth matches the Billing page, Starter's price is from the old plan-change modal, Enterprise is a guess
export const PLANS: Plan[] = [
    {
        id: 'starter', name: 'Starter', summary: 'Registry and announcement emails', price: 'A$4,900',
        features: [
            'Share registry sync with Automic',
            'Automatic ASX announcement emails · up to 2,000 investors',
            'Investor sign‑ups and contact forms',
            'Email support',
        ],
    },
    {
        id: 'growth', name: 'Growth', summary: 'The full platform — registry, communications and Website Hub', price: 'A$8,900',
        includes: 'Starter',
        features: [
            'Unlimited investors on announcement emails',
            'Email builder, campaigns and social posts',
            'All 11 website widgets and the installation checker',
            'Investor questions',
            'Analytics and board reports',
        ],
    },
    {
        id: 'enterprise', name: 'Enterprise', summary: 'For groups and dual‑listed companies', price: null,
        includes: 'Growth',
        features: [
            'More than one listing on one account',
            'Custom widgets and branded Website Hub',
            'Priority support and a named account manager',
        ],
    },
];

export const CURRENT_PLAN: Plan['id'] = 'growth';

// WordPress connection: the latest things Investi wrote to the client's site (no WordPress sync exists yet)
export interface WordPressActivity {
    title: string;
    note: string;
    // done = post written, link = media coverage (no post), failed = WordPress refused it
    kind: 'done' | 'link' | 'failed';
}

export const WP_ACTIVITY: WordPressActivity[] = [
    { title: 'Site visit recap — Gold Mountain', note: 'updated · 4 minutes ago', kind: 'done' },
    { title: 'Managing Director interview', note: 'created · 28 Aug', kind: 'done' },
    { title: 'Quarterly webinar recording', note: 'created · 14 Aug', kind: 'done' },
    { title: 'Mining Weekly coverage', note: 'link only — no post', kind: 'link' },
    { title: 'Board photo update', note: 'failed · image too large', kind: 'failed' },
];

export interface Invoice {
    // url key: /client-settings/billing/invoice?id=2026
    key: string;
    id: string;
    date: string;
    period: string;
    plan: string;
    // ex GST, in dollars
    subtotal: number;
    paid: string;
}

// sample invoices (no billing API yet)
export const INVOICES: Invoice[] = [
    { key: '2026', id: 'INV‑49M‑2026', date: '1 Sep 2026', period: '1 Sep 2026 – 31 Aug 2027', plan: 'Growth', subtotal: 8900, paid: '1 Sep 2026' },
    { key: '2025', id: 'INV‑49M‑2025', date: '1 Sep 2025', period: '1 Sep 2025 – 31 Aug 2026', plan: 'Growth', subtotal: 8900, paid: '2 Sep 2025' },
];

export const GST_RATE = 0.1;

export const money = (n: number) => `A$${n.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// what the invoice charges, incl. GST
export const invoiceTotal = (i: Invoice) => Math.round(i.subtotal * (1 + GST_RATE) * 100) / 100;

export interface BillingDetails {
    abn: string;
    po: string;
    contact: string;
    email: string;
    address: string;
    suburb: string;
    state: string;
    postcode: string;
}

export const BILLING: BillingDetails = {
    abn: '12 345 678 901',
    po: '',
    contact: 'Rob Fletcher',
    email: 'accounts@49metals.com.au',
    address: '6, 245 Churchill Avenue',
    suburb: 'Subiaco',
    state: 'WA',
    postcode: '6008',
};

// "49 Metals" from "49 Metals Limited"
const shortName = (name: string) => name.replace(/\s+(limited|ltd\.?|pty\.? ltd\.?|nl|inc\.?)$/i, '');

// the current client's identity as the pages print it
export const useClient = () => {
    const stock = useSelector((state: IGlobalState) => state.currentStock);
    const name = stock?.companyName || stock?.ticker || '';
    return {
        stock,
        name,
        short: shortName(name),
        ticker: stock?.ticker ?? '',
        exchange: stock?.exchange ?? '',
    };
};
